import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage } from '../../api';
import { reportService, type Report } from '../../api/report';
import { tripService, type Trip } from '../../api/trips';
import { useAuth } from '../../context/AuthContext';
import {
  ClipboardList,
  Search,
  Car,
  MapPin,
  CalendarDays,
  Clock,
  Gauge,
  Timer,
  FileCheck2,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  RotateCcw,
  Send,
  Save,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Input,
  Button,
  Badge,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
};

const formatMinutes = (minutes?: number) => {
  if (!minutes) return '-';
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (!hours) return `${remainingMinutes} min`;
  if (!remainingMinutes) return `${hours}h`;
  return `${hours}h ${remainingMinutes}min`;
};

const buildDefaultTitle = (trip: Trip) => `Relatório pós-uso - Viagem #${trip.id}`;

const getVehicleLabel = (trip: Trip) => {
  if (!trip.vehicle) return `Veículo #${trip.vehicle_id}`;
  return `${trip.vehicle.brand} - ${trip.vehicle.license_plate}`;
};

const RelatorioPosUso = () => {
  const { user, setPageTitle } = useAuth();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedTripId, setSelectedTripId] = useState<number | null>(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setPageTitle('Relatórios Pós-Uso');
  }, [setPageTitle]);

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(null), 3500);
      return () => clearTimeout(t);
    }
  }, [successMessage]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!user?.employee_id) {
      setTrips([]);
      setReports([]);
      setError('Seu usuário não está vinculado a um funcionário.');
      setLoading(false);
      return;
    }

    try {
      const [loadedTrips, loadedReports] = await Promise.all([
        tripService.getAll(),
        reportService.getAll(),
      ]);

      const concludedTrips = (loadedTrips || [])
        .filter(
          (trip) => trip.employee_id === user.employee_id && trip.status === 'concluida',
        )
        .sort((a, b) => {
          const dateA = new Date(a.end_date || a.updated_at).getTime();
          const dateB = new Date(b.end_date || b.updated_at).getTime();
          return dateB - dateA;
        });

      setTrips(concludedTrips);
      setReports((loadedReports || []).filter((report) => !!report.trip_id));
    } catch (err) {
      setTrips([]);
      setReports([]);
      setError(
        getApiErrorMessage(
          err,
          'Erro ao carregar viagens concluídas e relatórios pós-uso.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [user?.employee_id]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const reportsByTripId = useMemo(
    () =>
      new Map(
        reports
          .filter((report): report is Report & { trip_id: number } => !!report.trip_id)
          .map((report) => [report.trip_id, report]),
      ),
    [reports],
  );

  const filteredTrips = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return trips;
    return trips.filter(trip => {
      const hasReport = reportsByTripId.has(trip.id);
      const statusLabel = hasReport ? 'preenchido' : 'pendente';
      return (
        String(trip.id).includes(term) ||
        (trip.destination || '').toLowerCase().includes(term) ||
        (trip.purpose || '').toLowerCase().includes(term) ||
        (trip.vehicle?.brand || '').toLowerCase().includes(term) ||
        (trip.vehicle?.license_plate || '').toLowerCase().includes(term) ||
        statusLabel.includes(term) ||
        (typeof trip.final_mileage === 'number' && String(trip.final_mileage).includes(term))
      );
    });
  }, [trips, reportsByTripId, searchTerm]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredTrips.length / ITEMS_PER_PAGE)),
    [filteredTrips.length],
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredTrips.length, currentPage, totalPages]);

  const { paginatedTrips, pageRange } = useMemo(() => {
    const total = filteredTrips.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      paginatedTrips: filteredTrips.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredTrips, currentPage, totalPages]);

  const getPageNumbers = () => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  const selectedTrip = useMemo(
    () => trips.find((trip) => trip.id === selectedTripId) || null,
    [selectedTripId, trips],
  );

  const selectedReport = useMemo(
    () => (selectedTrip ? reportsByTripId.get(selectedTrip.id) || null : null),
    [reportsByTripId, selectedTrip],
  );

  const handleSelectTrip = useCallback(
    (trip: Trip) => {
      const report = reportsByTripId.get(trip.id);
      setSelectedTripId(trip.id);
      setContent(report?.content || '');
      setSuccessMessage(null);
      setError(null);
    },
    [reportsByTripId],
  );

  useEffect(() => {
    if (!trips.length) {
      setSelectedTripId(null);
      setContent('');
      return;
    }
    if (selectedTripId !== null && !trips.some((trip) => trip.id === selectedTripId)) {
      setSelectedTripId(null);
      setContent('');
    }
  }, [selectedTripId, trips]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (!selectedTrip) {
      setError('Selecione uma viagem concluída para preencher o relatório.');
      return;
    }

    if (!content.trim()) {
      setError('Descreva o pós-uso antes de salvar o relatório.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const title = buildDefaultTitle(selectedTrip);
      const trimmedContent = content.trim();

      if (selectedReport) {
        await reportService.update(selectedReport.id, { title, content: trimmedContent });
        setSuccessMessage('Relatório pós-uso atualizado com sucesso.');
      } else {
        await reportService.create({ trip_id: selectedTrip.id, title, content: trimmedContent });
        setSuccessMessage('Relatório pós-uso enviado com sucesso.');
      }

      await loadData();
      if (selectedTripId !== null) {
        setSelectedTripId(selectedTripId);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(
        getApiErrorMessage(err, 'Erro ao salvar o relatório pós-uso. Tente novamente.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Relatórios Pós-Uso" hideTitleInBody />

        <div className="flex flex-col gap-5">
          <div className="mb-2 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[15px] font-semibold leading-tight text-sky-600">Operações — Encerramento de viagens</p>
              <h1 className="mt-1 text-[22px] sm:text-[26px] font-bold leading-[1.15] tracking-tight text-slate-900">
                Relatórios pós-uso das suas viagens.
                <span className="ml-1.5 text-slate-500 font-semibold text-[18px] sm:text-[20px]">
                  Registre o encerramento de cada viagem concluída.
                </span>
              </h1>
              <p className="mt-2 text-[14px] leading-relaxed text-slate-500 max-w-3xl">
                Selecione uma viagem concluída e preencha o relatório com observações sobre a devolução do veículo, limpeza, avarias percebidas, quilometragem final e demais informações relevantes.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="secondary" size="lg" onClick={() => void loadData()} disabled={loading || submitting}>
                <RefreshCw className={`h-[18px] w-[18px] stroke-[2.1] ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </div>
          </div>

          {error && (
            <div className="inline-flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
              <div className="flex flex-col gap-0.5">
                <span className="text-[13px] font-bold leading-tight">Não foi possível prosseguir</span>
                <span className="text-[12.5px] font-medium leading-tight text-rose-700/90">{error}</span>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="inline-flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800 animate-[fadeIn_0.2s_ease-out]">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <div className="flex flex-col gap-0.5">
                <span className="text-[13px] font-bold leading-tight">Sucesso</span>
                <span className="text-[12.5px] font-medium leading-tight text-emerald-800/90">{successMessage}</span>
              </div>
            </div>
          )}

          {loading ? (
            <SectionCard>
              <CardBody>
                <div className="flex items-center justify-center gap-2.5 py-20">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  <span className="text-[13.5px] font-semibold text-slate-600">Carregando relatórios pós-uso...</span>
                </div>
              </CardBody>
            </SectionCard>
          ) : trips.length === 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-[18px] w-[18px] shrink-0 mt-[1px] stroke-[1.9]" />
                <div>
                  <p className="font-semibold mb-0.5">Nenhuma viagem concluída</p>
                  <p className="text-[13px] text-amber-800/90">
                    Não há viagens concluídas disponíveis para relatório no seu escopo atual.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.2fr_1fr] 2xl:grid-cols-[1.3fr_1fr]">
              <SectionCard>
                <CardHeader>
                  <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-sky-600 text-white shadow-[0_6px_16px_-8px_rgba(79,70,229,0.6)]">
                        <ClipboardList className="h-[20px] w-[20px] stroke-[1.9]" />
                      </div>
                      <div>
                        <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Viagens concluídas</h2>
                        <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                          Selecione uma viagem para preencher/editar seu relatório
                        </p>
                      </div>
                    </div>
                    <div className="relative w-full sm:w-[360px] shrink-0">
                      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        placeholder="Pesquisar destino, veículo, placa, status..."
                        className="!pl-10 h-10"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <ModernTable className="table-fixed layout-fixed">
                    <Thead>
                      <Tr>
                        <Th className="w-[9%] min-w-[90px] max-w-[110px]">ID</Th>
                        <Th className="w-[19%] min-w-[190px] max-w-[260px]">Destino</Th>
                        <Th className="w-[17%] min-w-[170px] max-w-[230px]">Veículo</Th>
                        <Th className="w-[15%] min-w-[160px] max-w-[210px]">Fim</Th>
                        <Th className="w-[12%] min-w-[130px] max-w-[170px]">Km final</Th>
                        <Th className="w-[10%] min-w-[100px] max-w-[140px]">Uso</Th>
                        <Th className="w-[10%] min-w-[120px] max-w-[160px]">Relatório</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {paginatedTrips.length === 0 ? (
                        <Tr>
                          <Td colSpan={7}>
                            <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                                <ClipboardList className="h-7 w-7 stroke-[1.8]" />
                              </div>
                              <div className="text-[14px] font-bold text-slate-700">Nenhuma viagem encontrada</div>
                              <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                                {searchTerm
                                  ? 'Nenhum resultado para a busca atual. Tente outro termo.'
                                  : 'Todas as viagens já foram processadas.'}
                              </p>
                            </div>
                          </Td>
                        </Tr>
                      ) : (
                        paginatedTrips.map((trip) => {
                          const hasReport = reportsByTripId.has(trip.id);
                          const isSelected = trip.id === selectedTripId;
                          return (
                            <Tr
                              key={trip.id}
                              className={isSelected ? '!bg-blue-50/70 !shadow-[inset_3px_0_0_0_rgba(37,99,235,0.6)]' : ''}
                            >
                              <Td>
                                <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[12px] font-bold tabular-nums text-slate-700 ring-1 ring-slate-200">
                                  #{trip.id}
                                </span>
                              </Td>
                              <Td>
                                <div className="min-w-0 w-full max-w-[260px] overflow-hidden">
                                  <div className="flex items-start gap-2">
                                    <MapPin className="mt-[3px] h-3.5 w-3.5 shrink-0 text-slate-400 stroke-[1.8]" />
                                    <div className="min-w-0 flex-1">
                                      <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={trip.destination || '-'}>
                                        {trip.destination || '-'}
                                      </span>
                                      {trip.purpose ? (
                                        <span className="mt-0.5 block truncate text-[11.5px] font-medium leading-tight text-slate-500" title={trip.purpose}>
                                          {trip.purpose}
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              </Td>
                              <Td>
                                <div className="min-w-0 w-full max-w-[230px] overflow-hidden">
                                  <div className="flex items-start gap-2">
                                    <div className="mt-0.5 shrink-0 flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                                      <Car className="h-[15px] w-[15px] stroke-[1.9]" />
                                    </div>
                                    <span className="block truncate text-[13px] font-semibold leading-tight text-slate-800 pt-[2px]" title={getVehicleLabel(trip)}>
                                      {getVehicleLabel(trip)}
                                    </span>
                                  </div>
                                </div>
                              </Td>
                              <Td>
                                <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                  <CalendarDays className="h-[15px] w-[15px] text-slate-400 stroke-[1.8]" />
                                  {formatDateTime(trip.end_date || trip.updated_at)}
                                </div>
                              </Td>
                              <Td>
                                <div className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 tabular-nums">
                                  <Gauge className="h-[15px] w-[15px] text-indigo-500 stroke-[1.8]" />
                                  {typeof trip.final_mileage === 'number'
                                    ? `${trip.final_mileage.toLocaleString('pt-BR')} km`
                                    : '-'}
                                </div>
                              </Td>
                              <Td>
                                <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                  <Timer className="h-[15px] w-[15px] text-slate-400 stroke-[1.8]" />
                                  {formatMinutes(trip.usage_duration_minutes)}
                                </div>
                              </Td>
                              <Td>
                                <button
                                  type="button"
                                  onClick={() => handleSelectTrip(trip)}
                                  className="group inline-flex items-center gap-1.5 transition-all"
                                >
                                  {hasReport ? (
                                    <Badge tone="emerald" className="group-hover:ring-emerald-300 gap-1.5 !pr-3">
                                      <FileCheck2 className="h-3 w-3 stroke-[2]" />
                                      Editar
                                    </Badge>
                                  ) : (
                                    <Badge tone="amber" className="group-hover:ring-amber-300 gap-1.5 !pr-3">
                                      <ClipboardList className="h-3 w-3 stroke-[2]" />
                                      Preencher
                                    </Badge>
                                  )}
                                </button>
                              </Td>
                            </Tr>
                          );
                        })
                      )}
                    </Tbody>
                  </ModernTable>

                  {pageRange.total > 0 ? (
                    <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                        Mostrando{' '}
                        <span className="font-bold tabular-nums text-slate-800">{pageRange.start}</span>
                        {'–'}{' '}
                        <span className="font-bold tabular-nums text-slate-800">{pageRange.end}</span>
                        {' de '}
                        <span className="font-bold tabular-nums text-slate-800">{pageRange.total}</span>
                        {' viagem(ns) concluída(s)'}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                          aria-label="Primeira página"
                        >
                          <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                          aria-label="Página anterior"
                        >
                          <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                        </button>
                        <div className="inline-flex items-center gap-1 mx-1">
                          {getPageNumbers().map((n, idx) =>
                            n === '...' ? (
                              <span key={`dots-${idx}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]">…</span>
                            ) : (
                              <button
                                key={n}
                                type="button"
                                onClick={() => setCurrentPage(n)}
                                disabled={submitting}
                                className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1 ${
                                  n === currentPage
                                    ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                    : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                                }`}
                              >
                                {n}
                              </button>
                            ),
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                          aria-label="Próxima página"
                        >
                          <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                          className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                          aria-label="Última página"
                        >
                          <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                        </button>
                      </div>
                    </div>
                  ) : null}
                </CardBody>
              </SectionCard>

              <SectionCard>
                <CardHeader>
                  <div className="flex items-start gap-3 w-full">
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-white shadow-[0_6px_16px_-8px_rgba(16,185,129,0.6)] ${selectedReport ? 'bg-gradient-to-br from-emerald-500 via-teal-500 to-emerald-600' : 'bg-gradient-to-br from-amber-500 via-orange-500 to-orange-600'}`}>
                      {selectedReport ? (
                        <FileCheck2 className="h-[20px] w-[20px] stroke-[1.9]" />
                      ) : (
                        <ClipboardList className="h-[20px] w-[20px] stroke-[1.9]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                        {selectedReport ? 'Editar relatório pós-uso' : 'Novo relatório pós-uso'}
                      </h2>
                      <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                        {selectedTrip ? `Viagem #${selectedTrip.id} selecionada` : 'Selecione uma viagem ao lado para começar'}
                      </p>
                    </div>
                    {selectedTrip ? (
                      selectedReport ? (
                        <Badge tone="emerald" className="gap-1.5">
                          <FileCheck2 className="h-3 w-3 stroke-[2]" />
                          Já preenchido
                        </Badge>
                      ) : (
                        <Badge tone="amber" className="gap-1.5">
                          <Clock className="h-3 w-3 stroke-[2]" />
                          Pendente
                        </Badge>
                      )
                    ) : null}
                  </div>
                </CardHeader>
                <CardBody>
                  {selectedTrip ? (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Viagem</div>
                          <div className="mt-1 flex items-center gap-2 text-sm text-slate-900">
                            <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[12px] font-bold tabular-nums text-slate-700 ring-1 ring-slate-200">
                              #{selectedTrip.id}
                            </span>
                            <MapPin className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                            <span className="truncate font-semibold" title={selectedTrip.destination || '-'}>
                              {selectedTrip.destination || '-'}
                            </span>
                          </div>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Veículo</div>
                          <div className="mt-1 flex items-center gap-2 text-sm text-slate-900">
                            <Car className="h-3.5 w-3.5 stroke-[1.9] text-blue-600" />
                            <span className="truncate font-semibold" title={getVehicleLabel(selectedTrip)}>
                              {getVehicleLabel(selectedTrip)}
                            </span>
                          </div>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Destino</div>
                          <div className="mt-1 text-sm text-slate-900 font-medium truncate" title={selectedTrip.destination || '-'}>
                            {selectedTrip.destination || '-'}
                          </div>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Tempo de uso</div>
                          <div className="mt-1 flex items-center gap-2 text-sm text-slate-900 font-semibold tabular-nums">
                            <Timer className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                            {formatMinutes(selectedTrip.usage_duration_minutes)}
                          </div>
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="report-content"
                          className="mb-2 inline-flex items-center gap-1 text-[13px] font-semibold text-slate-800"
                        >
                          <FileCheck2 className="h-4 w-4 stroke-[1.9] text-indigo-600" />
                          Descrição do pós-uso
                          <span className="ml-0.5 text-rose-500 font-bold leading-none">*</span>
                        </label>
                        <textarea
                          id="report-content"
                          rows={14}
                          value={content}
                          onChange={(event) => setContent(event.target.value)}
                          className="w-full resize-y rounded-xl border border-slate-200 bg-white px-4 py-3 text-[13.5px] leading-relaxed text-slate-900 transition focus:border-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-500/10"
                          placeholder="Descreva como o veículo foi devolvido, observações de uso, limpeza interna e externa, avarias percebidas ou qualquer informação relevante sobre o encerramento da viagem. Inclua detalhes sobre abastecimento, pneus, documentação etc."
                          disabled={submitting}
                        />
                        <div className="mt-1.5 flex justify-between text-[11px] font-medium text-slate-500">
                          <span>Seja objetivo — o gestor da frota lerá este relatório.</span>
                          <span className="tabular-nums">{content.length} caracteres</span>
                        </div>
                      </div>

                      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center pt-1">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => {
                            if (selectedTrip) handleSelectTrip(selectedTrip);
                          }}
                          disabled={submitting}
                          className="h-10 px-5"
                        >
                          <RotateCcw className="h-[17px] w-[17px] stroke-[1.9]" />
                          Restaurar
                        </Button>
                        <Button
                          type="submit"
                          variant="primary"
                          isLoading={submitting}
                          loadingLabel="Salvando..."
                          className="h-10 px-5"
                        >
                          {selectedReport ? (
                            <Fragment>
                              <Save className="h-[17px] w-[17px] stroke-[1.9]" />
                              Atualizar relatório
                            </Fragment>
                          ) : (
                            <Fragment>
                              <Send className="h-[17px] w-[17px] stroke-[1.9]" />
                              Enviar relatório
                            </Fragment>
                          )}
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
                      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 text-slate-400 shadow-[inset_0_2px_4px_rgba(15,23,42,0.04)]">
                        <ClipboardList className="h-8 w-8 stroke-[1.8]" />
                      </div>
                      <div>
                        <div className="text-[14px] font-bold text-slate-700">Nenhuma viagem selecionada</div>
                        <p className="mt-1 max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                          Clique em <span className="font-semibold text-amber-700">"Preencher"</span> ou
                          <span className="font-semibold text-emerald-700"> "Editar"</span> em uma viagem
                          ao lado para começar o relatório.
                        </p>
                      </div>
                    </div>
                  )}
                </CardBody>
              </SectionCard>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RelatorioPosUso;
